import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["zip", "address", "neighborhood", "country", "state", "city", "stateName", "cityName", "countryName", "latitude", "longitude", "status", "number"]

  static values = {
    fetchUrl: String,
    refreshStatesUrl: String,
    refreshCitiesUrl: String,
    statesFrame: String,
    citiesFrame: String
  }

  async search(event) {
    if (event?.detail?.fromMap) return

    const input = event.target
    const zip = this.normalizeZip(input)

    if (!zip.length) {
      this.clearStatus()
      this.clearAddressFields()
      return
    }

    if (zip.length !== 8) {
      this.showStatus("CEP inválido: informe 8 dígitos.", "text-danger")
      this.clearAddressFields()
      return
    }

    if (!this.hasFetchUrlValue) return

    this.showStatus("Buscando CEP...", "text-secondary", true)

    const url = this.fetchUrlValue.includes("{zip}")
      ? this.fetchUrlValue.replace("{zip}", zip)
      : `${this.fetchUrlValue}${zip}`

    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error("CEP não encontrado")

      const data = await response.json()

      if (this.hasAddressTarget) this.addressTarget.value = data.logradouro || ""
      if (this.hasNeighborhoodTarget) this.neighborhoodTarget.value = data.bairro || ""
      if (this.hasLatitudeTarget) this.latitudeTarget.value = data.latitude || ""
      if (this.hasLongitudeTarget) this.longitudeTarget.value = data.longitude || ""
      this.clearStatus()

      if (this.hasCountryTarget) {
        this.countryTarget.value = data.country_id || ""
        this.syncCountry()
        if (this.countryTarget.value && this.hasRefreshStatesUrlValue && this.hasStatesFrameValue) {
          await this.visitFrame(`${this.refreshStatesUrlValue}${this.countryTarget.value}`, this.statesFrameValue)
        }
      }

      if (this.hasStateTarget) {
        this.fillState(data)

        if (this.hasRefreshCitiesUrlValue && this.hasCitiesFrameValue && this.stateTarget.value) {
          await this.visitFrame(`${this.refreshCitiesUrlValue}${this.stateTarget.value}`, this.citiesFrameValue)
        }
      }

      if (this.hasCityTarget) this.fillCity(data)

      if (this.hasNumberTarget) this.numberTarget.focus()
    } catch (error) {
      console.error("CEP lookup falhou", error)
      this.showStatus("CEP não encontrado ou serviço indisponível.", "text-danger")
      this.clearAddressFields()
    }
  }

  normalizeZip(input) {
    if (!input) return ""
    const digits = input.value.replace(/\D/g, "").slice(0, 8)
    const masked = digits.replace(/(\d{5})(\d{1,3})/, "$1-$2")
    input.value = masked
    return digits
  }

  fillState(data) {
    const stateId = data.state_id?.toString()
    const stateUf = data.uf || ""

    if (this.stateTarget.tagName === "SELECT") {
      if (stateId && this.optionExists(this.stateTarget, stateId)) {
        this.stateTarget.value = stateId
        this.syncState()
        return
      }

      if (stateUf && this.optionExists(this.stateTarget, stateUf)) {
        this.stateTarget.value = stateUf
        this.syncState()
        return
      }
    }

    this.stateTarget.value = stateId || stateUf || ""
    this.syncState()
  }

  fillCity(data) {
    const cityId = data.city_id?.toString()
    const cityName = data.localidade || ""

    if (this.cityTarget.tagName === "SELECT") {
      if (cityId && this.optionExists(this.cityTarget, cityId)) {
        this.cityTarget.value = cityId
        return
      }
    }

    this.cityTarget.value = cityId || cityName
    this.syncCity()
  }

  optionExists(select, value) {
    return Array.from(select.options).some((option) => option.value === value)
  }

  syncState() {
    if (!this.hasStateTarget || !this.hasStateNameTarget) return

    const selected = this.stateTarget.tagName === "SELECT"
      ? this.stateTarget.selectedOptions[0]
      : null

    const text = selected?.dataset.uf || selected?.textContent || this.stateTarget.value
    this.stateNameTarget.value = text || ""
  }

  syncCity() {
    if (!this.hasCityTarget || !this.hasCityNameTarget) return

    const selected = this.cityTarget.tagName === "SELECT"
      ? this.cityTarget.selectedOptions[0]
      : null

    const text = selected?.textContent || this.cityTarget.value
    this.cityNameTarget.value = text || ""
  }

  syncCountry() {
    if (!this.hasCountryTarget || !this.hasCountryNameTarget) return

    const selected = this.countryTarget.tagName === "SELECT"
      ? this.countryTarget.selectedOptions[0]
      : null

    const text = selected?.textContent || this.countryTarget.value
    this.countryNameTarget.value = text || ""
  }

  visitFrame(url, frameId) {
    const frame = document.getElementById(frameId)
    if (!frame) return Turbo.visit(url, { frame: frameId })

    return new Promise((resolve) => {
      const onLoad = () => resolve()
      frame.addEventListener("turbo:frame-load", onLoad, { once: true })
      Turbo.visit(url, { frame: frameId })
    })
  }

  showStatus(message, textClass = "text-secondary", withSpinner = false) {
    if (!this.hasStatusTarget) return
    const spinner = withSpinner
      ? '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>'
      : ""
    this.statusTarget.innerHTML = `${spinner}${message}`
    this.statusTarget.classList.remove("text-secondary", "text-danger")
    this.statusTarget.classList.add(textClass)
  }

  clearStatus() {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = ""
    this.statusTarget.classList.remove("text-secondary", "text-danger")
  }

  clearAddressFields() {
    if (this.hasAddressTarget) this.addressTarget.value = ""
    if (this.hasNeighborhoodTarget) this.neighborhoodTarget.value = ""
    if (this.hasNumberTarget) this.numberTarget.value = ""
    if (this.hasLatitudeTarget) this.latitudeTarget.value = ""
    if (this.hasLongitudeTarget) this.longitudeTarget.value = ""

    if (this.hasCityTarget) this.cityTarget.value = ""
    if (this.hasStateTarget) this.stateTarget.value = ""
    if (this.hasCountryTarget) this.countryTarget.value = ""

    if (this.hasCityNameTarget) this.cityNameTarget.value = ""
    if (this.hasStateNameTarget) this.stateNameTarget.value = ""
    if (this.hasCountryNameTarget) this.countryNameTarget.value = ""
  }
}
